import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LanguageOption {
  code: string;
  name: string;
  direction: 'ltr' | 'rtl';
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  availableLanguages: LanguageOption[] = [
    { code: 'fr', name: 'Français', direction: 'ltr' },
    { code: 'en', name: 'English', direction: 'ltr' },
    { code: 'ar', name: 'العربية', direction: 'rtl' }
  ];

  private translations: { [key: string]: { [key: string]: string } } = {
    fr: {},
    en: {},
    ar: {}
  };

  private currentLanguageSubject = new BehaviorSubject<LanguageOption>(this.availableLanguages[0]);
  currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor() {
    this.loadTranslations();
    // Try to load language from localStorage or use browser language
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    // Vérifier si on est dans un environnement navigateur
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    
    const savedLang = localStorage.getItem('language');
    
    if (savedLang) {
      const languageOption = this.availableLanguages.find(lang => lang.code === savedLang);
      if (languageOption) {
        this.setLanguage(languageOption.code);
        return;
      }
    }
    
    // If no saved language, try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    const supportedLang = this.availableLanguages.find(lang => lang.code === browserLang);
    
    if (supportedLang) {
      this.setLanguage(supportedLang.code);
    } else {
      // Default to French if no match
      this.setLanguage('fr');
    }
  }

  setLanguage(langCode: string): void {
    const languageOption = this.availableLanguages.find(lang => lang.code === langCode);
    if (languageOption) {
      this.currentLanguageSubject.next(languageOption);
      
      // Vérifier si on est dans un environnement navigateur
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem('language', langCode);
      }
      
      if (typeof document !== 'undefined') {
        document.documentElement.lang = langCode;
        document.documentElement.dir = languageOption.direction;
        document.body.classList.toggle('rtl', languageOption.direction === 'rtl');
      }
    }
  }

  private loadTranslations(): void {
    this.translations['fr'] = {
      'accueil': 'Accueil',
      'services': 'Services',
      'about': 'À propos',
      'contact': 'Contact',
      'connexion': 'Connexion',
      'hero_title': 'Transformez votre Business en 2025',
      'hero_subtitle': 'Solutions AI & Data pour un commerce intelligent',
      'hero_description': 'Découvrez comment Codincloud peut propulser votre entreprise vers l\'avenir.',
      'start_free_trial': 'Démarrer l\'essai gratuit',
      'features_title': 'Nos fonctionnalités avancées',
      'features_subtitle': 'Pensées pour l\'avenir du e-commerce',
      'feature1_title': 'Installation par IA',
      'feature1_desc': 'Configurez votre boutique en quelques secondes avec l\'IA',
      'feature2_title': 'Analytique Prédictive',
      'feature2_desc': 'Anticipez les besoins de vos clients grâce au Machine Learning',
      'feature3_title': 'Support 24/7',
      'feature3_desc': 'Notre assistant AI multilingue est disponible en continu',
      'vision_title': 'Notre Vision',
      'vision_subtitle': 'Une équipe mondiale, une mission unique',
      'vision_desc': 'Nous utilisons les principes de l\'informatique quantique avec un design centré utilisateur.',
      'stats_clients': 'Clients satisfaits',
      'stats_uptime': 'Temps de disponibilité',
      'contact_title': 'Discutons de votre futur',
      'footer_copyright': '© 2026 vendeur.site — Propulsé par Codincloud',

      // ─── Landing publique vendeur.site (refonte) ───
      'lp_nav_features': 'Fonctionnalités',
      'lp_nav_steps': 'Comment ça marche',
      'lp_nav_pricing': 'Tarifs',
      'lp_nav_testimonials': 'Témoignages',
      'lp_nav_faq': 'FAQ',
      'lp_nav_login': 'Se connecter',
      'lp_nav_signup': 'Créer mon site',

      'lp_hero_eyebrow': 'Nouveau · IA Claude Opus 4.5 intégrée',
      'lp_hero_title_pre': 'Lancez votre boutique en ligne en',
      'lp_hero_title_highlight': '2 minutes',
      'lp_hero_title_post': '— guidée par l\'IA',
      'lp_hero_subtitle': 'Vendeur.site combine site builder, inbox multi-canal (Messenger, Instagram, WhatsApp, Marketplace), gestion produits/commandes/livraisons (TunisiaShip) et un assistant IA qui code votre site pour vous. 100% tunisien, 0% technique.',
      'lp_hero_cta_primary': 'Créer ma boutique gratuitement',
      'lp_hero_cta_secondary': 'Voir une démo en 60s',
      'lp_hero_stat_vendors': 'Vendeurs actifs',
      'lp_hero_stat_setup': 'Minutes pour créer',
      'lp_hero_stat_ai': 'IA Claude intégrée',

      'lp_cinema_eyebrow': 'Expérience immersive',
      'lp_cinema_title': 'Votre boutique prend vie — cliquez dans la vidéo',
      'lp_cinema_sub': 'Une vidéo générée par notre IA. Les points lumineux sont de vrais boutons : explorez, cliquez, lancez-vous.',
      'lp_cinema_play': 'Lancer l\'expérience',
      'lp_cinema_hs_signup': 'Créer ma boutique',
      'lp_cinema_hs_inbox': 'Inbox multicanal',
      'lp_cinema_hs_studio': 'Studio IA en action',
      'lp_cinema_chap_1': 'La boutique s\'assemble',
      'lp_cinema_chap_2': 'Canaux connectés',
      'lp_cinema_chap_3': 'IA aux commandes',

      'lp_trust_title': 'Ils nous font confiance',

      'lp_features_title': 'Tout ce dont vous avez besoin pour vendre en ligne',
      'lp_features_subtitle': 'Une plateforme unifiée. Pas de stack à assembler. Pas de code à écrire.',
      'lp_feature_builder_title': 'Site builder IA',
      'lp_feature_builder_desc': 'Décrivez votre boutique, l\'IA la construit. Modifiez-la en parlant : « Ajoute une FAQ », « Change la couleur en bleu ». Preview live.',
      'lp_feature_inbox_title': 'Inbox multi-canal',
      'lp_feature_inbox_desc': 'Messenger + Instagram + WhatsApp + Marketplace en un seul écran. Répondez 3× plus vite à vos clients.',
      'lp_feature_360_title': 'Gestion 360°',
      'lp_feature_360_desc': 'Produits, stocks, commandes, clients, employés, livreurs. Tout est connecté, tout est en temps réel.',
      'lp_feature_shipping_title': 'Livraisons Tunisie',
      'lp_feature_shipping_desc': 'Intégration TunisiaShip native : étiquettes, suivi, retours. Comparez les tarifs et imprimez en 1 clic.',
      'lp_feature_analytics_title': 'Analytics & RFM',
      'lp_feature_analytics_desc': 'Top produits, top clients, funnel de conversion, segmentation RFM. Drill-down infini, exports CSV.',
      'lp_feature_versioning_title': 'Versioning Git-like',
      'lp_feature_versioning_desc': 'Chaque modification est sauvegardée. Restaurez votre site à une version précédente en 1 clic.',
      'lp_feature_tiktok_title': 'TikTok arrive bientôt',
      'lp_feature_tiktok_desc': 'Reels, hashtags IA, TikTok Ads, TikTok Shop, inbox unifiée commentaires + DM — la même expérience que pour Meta.',
      'lp_feature_soon_badge': 'Bientôt',
      'lp_feature_notify_me': 'Me prévenir',
      'lp_feature_learn_more': 'En savoir plus',

      'lp_steps_title': 'Comment ça marche',
      'lp_steps_subtitle': 'Quatre étapes pour passer de l\'idée à votre boutique en ligne.',
      'lp_step1_title': 'Décrivez votre boutique',
      'lp_step1_desc': 'Onboarding 7 étapes : nom, boutique(s), template, branding, pages, intégrations.',
      'lp_step2_title': 'L\'IA construit votre site',
      'lp_step2_desc': 'Claude Opus 4.5 génère pages et blocs, applique votre charte, sélectionne vos meilleurs produits.',
      'lp_step3_title': 'Vous affinez en discutant',
      'lp_step3_desc': 'Studio IA conversationnel + preview live. « Ajoute une promo -10% », « Change la typo en moderne ».',
      'lp_step4_title': 'Lancez-vous',
      'lp_step4_desc': 'Publication en 1 clic. URL <vous>.vendeur.site gratuite, domaine custom disponible.',

      'lp_demo_title': 'Voyez l\'IA en action',
      'lp_demo_subtitle': 'Cliquez sur une suggestion ci-dessous — la preview change en direct.',
      'lp_demo_browser_url': 'monstore.vendeur.site',
      'lp_demo_ai_greeting': 'Salut ! Je suis votre assistant. Que voulez-vous modifier ?',
      'lp_demo_suggestion_color': 'Change la couleur en cyan',
      'lp_demo_suggestion_promo': 'Ajoute une promo -10%',
      'lp_demo_suggestion_hero': 'Mets ce produit en hero',
      'lp_demo_user_color': 'Change la couleur principale en cyan',
      'lp_demo_ai_color': 'Couleur principale mise à jour en cyan. La preview s\'actualise.',
      'lp_demo_user_promo': 'Ajoute une promo -10% sur la page d\'accueil',
      'lp_demo_ai_promo': 'Badge promo -10% ajouté en haut de votre boutique.',
      'lp_demo_user_hero': 'Mets ce produit en hero',
      'lp_demo_ai_hero': 'Produit promu en bannière hero avec un visuel fort.',
      'lp_demo_powered': 'Propulsé par Claude Opus 4.5',

      'lp_pricing_title': 'Des tarifs simples, transparents',
      'lp_pricing_subtitle': 'Commencez gratuitement. Évoluez quand vous êtes prêt.',
      'lp_pricing_popular': 'Le plus populaire',
      'lp_plan_starter_name': 'Starter',
      'lp_plan_starter_price': 'Gratuit',
      'lp_plan_starter_period': 'Pour toujours',
      'lp_plan_starter_f1': '1 site vendeur.site',
      'lp_plan_starter_f2': '50 produits',
      'lp_plan_starter_f3': '1 boutique physique',
      'lp_plan_starter_f4': 'IA limitée (50 messages/h)',
      'lp_plan_starter_f5': 'Support communauté',
      'lp_plan_starter_cta': 'Commencer gratuitement',
      'lp_plan_pro_name': 'Pro',
      'lp_plan_pro_price': '49 TND',
      'lp_plan_pro_period': '/mois',
      'lp_plan_pro_f1': 'Sites & produits illimités',
      'lp_plan_pro_f2': 'IA Claude Opus 4.5',
      'lp_plan_pro_f3': 'Inbox multi-canal complète',
      'lp_plan_pro_f4': 'TunisiaShip intégré',
      'lp_plan_pro_f5': 'Analytics RFM avancées',
      'lp_plan_pro_f6': 'Support prioritaire',
      'lp_plan_pro_cta': 'Essai 14 jours',
      'lp_plan_business_name': 'Business',
      'lp_plan_business_price': 'Sur devis',
      'lp_plan_business_period': 'Multi-vendeurs',
      'lp_plan_business_f1': 'Multi-vendeurs',
      'lp_plan_business_f2': 'Employés illimités',
      'lp_plan_business_f3': 'API & webhooks',
      'lp_plan_business_f4': 'Support dédié 24/7',
      'lp_plan_business_f5': 'Formation sur site',
      'lp_plan_business_cta': 'Nous contacter',

      'lp_testimonials_title': 'Ce que nos vendeurs en disent',
      'lp_testimonials_subtitle': 'Histoires vraies, ventes vraies.',
      'lp_testimonial_1_quote': 'J\'ai créé mon site en 1h et fait ma première vente le soir même. L\'IA m\'a tout expliqué simplement.',
      'lp_testimonial_1_name': 'Sarra B.',
      'lp_testimonial_1_role': 'Boutique mode, Tunis',
      'lp_testimonial_2_quote': 'Le chat WhatsApp intégré change la donne. Mes clients commandent directement sans passer par l\'agence.',
      'lp_testimonial_2_name': 'Mehdi T.',
      'lp_testimonial_2_role': 'Épicerie bio, Sousse',
      'lp_testimonial_3_quote': 'Le suivi TunisiaShip me fait gagner 1h/jour. Et l\'IA me suggère les produits à mettre en avant.',
      'lp_testimonial_3_name': 'Yasmine L.',
      'lp_testimonial_3_role': 'Cosmétiques, Sfax',
      'lp_testimonial_4_quote': 'Avant je passais des heures sur Photoshop. Maintenant je décris ce que je veux et c\'est fait.',
      'lp_testimonial_4_name': 'Karim H.',
      'lp_testimonial_4_role': 'Artisanat, Nabeul',
      'lp_testimonial_5_quote': 'Les analytics m\'ont permis de comprendre mes top clients. Mon CA a doublé en 3 mois.',
      'lp_testimonial_5_name': 'Amira K.',
      'lp_testimonial_5_role': 'Bijouterie, Hammamet',

      'lp_faq_title': 'Questions fréquentes',
      'lp_faq_subtitle': 'Vous hésitez encore ? Voici les réponses aux questions les plus posées.',
      'lp_faq_1_q': 'Combien coûte vendeur.site ?',
      'lp_faq_1_a': 'Le plan Starter est gratuit pour toujours. Le plan Pro est à 49 TND/mois (sans engagement). Business est sur devis selon vos besoins.',
      'lp_faq_2_q': 'L\'IA peut-elle vraiment construire mon site ?',
      'lp_faq_2_a': 'Oui. Notre assistant utilise Claude Opus 4.5, le modèle d\'IA le plus avancé pour le code. Il génère pages, blocs, applique votre charte graphique et peut modifier votre site en parlant français, arabe ou anglais.',
      'lp_faq_3_q': 'Mes données sont-elles sécurisées ?',
      'lp_faq_3_a': 'Toutes vos données (produits, clients, conversations) sont hébergées en Tunisie sur infrastructure dédiée. Chiffrement SSL, sauvegardes quotidiennes, conformité aux meilleures pratiques.',
      'lp_faq_4_q': 'Puis-je connecter mon nom de domaine ?',
      'lp_faq_4_a': 'Bien sûr. Vous recevez gratuitement une URL <vous>.vendeur.site, et vous pouvez connecter votre propre domaine (ex : monstore.tn) à partir du plan Pro.',
      'lp_faq_5_q': 'Vous intégrez Konnect / Stripe ?',
      'lp_faq_5_a': 'Konnect (paiement local Tunisie) est intégré. Stripe arrive bientôt. Le paiement à la livraison est supporté nativement avec TunisiaShip.',
      'lp_faq_6_q': 'Quels canaux sociaux supportez-vous ?',
      'lp_faq_6_a': 'Facebook Messenger, Instagram Direct, WhatsApp Business (Cloud API officielle) et Facebook Marketplace. Toutes vos conversations sont unifiées dans une seule inbox.',
      'lp_faq_7_q': 'Comment l\'IA est-elle facturée ?',
      'lp_faq_7_a': 'L\'IA est incluse dans le plan Pro (illimitée à usage raisonnable). Aucune surprise sur la facture, c\'est transparent.',

      'lp_cta_final_title': 'Prêt à lancer votre boutique ?',
      'lp_cta_final_subtitle': '2 minutes pour créer, des années pour grandir.',
      'lp_cta_final_primary': 'Créer ma boutique',
      'lp_cta_final_secondary': 'Demander une démo',

      'lp_footer_tagline': 'La plateforme tunisienne tout-en-un pour vendeurs e-commerce, propulsée par l\'IA.',
      'lp_footer_col_product': 'Produit',
      'lp_footer_col_resources': 'Ressources',
      'lp_footer_col_legal': 'Légal',
      'lp_footer_link_builder': 'Site builder',
      'lp_footer_link_inbox': 'Inbox',
      'lp_footer_link_analytics': 'Analytics',
      'lp_footer_link_shipping': 'TunisiaShip',
      'lp_footer_link_pricing': 'Tarifs',
      'lp_footer_link_docs': 'Documentation',
      'lp_footer_link_templates': 'Templates',
      'lp_footer_link_blog': 'Blog',
      'lp_footer_link_changelog': 'Changelog',
      'lp_footer_link_terms': 'Conditions',
      'lp_footer_link_privacy': 'Confidentialité',
      'lp_footer_link_cookies': 'Cookies',
      'lp_footer_link_contact': 'Contact',

      'lp_chatbot_title': 'Assistant vendeur.site',
      'lp_chatbot_status': 'En ligne',
      'lp_chatbot_powered': 'Propulsé par Claude Opus',
      'lp_chatbot_quick_questions': 'Questions fréquentes',
      'lp_chatbot_contact_direct': 'Contact direct',
      'lp_chatbot_contact_title': 'Contactez-nous directement',
      'lp_chatbot_welcome': 'Bonjour ! Je suis l\'assistant vendeur.site. Comment puis-je vous aider à lancer votre boutique ?',
      'lp_chatbot_q1': 'Quels sont vos services principaux ?',
      'lp_chatbot_a1': 'Vendeur.site est une plateforme tout-en-un : site builder IA, inbox multi-canal (Messenger, Instagram, WhatsApp, Marketplace), gestion produits/commandes/livraisons, intégration TunisiaShip et analytics avancées.',
      'lp_chatbot_q2': 'Combien coûtent vos services ?',
      'lp_chatbot_a2': 'Découverte : 0 TND/mois (7 % sur les ventes). Starter : 29 TND/mois (5 %). Pro : 99 TND/mois (3 %). Enterprise : 299 TND/mois (2 %). Sans engagement.',
      'lp_chatbot_q3': 'Proposez-vous un essai gratuit ?',
      'lp_chatbot_a3': 'Oui ! L\'inscription est gratuite et le plan Découverte est à 0 TND/mois — vous ne payez qu\'une commission sur les ventes. Sans engagement.',
      'lp_chatbot_q4': 'Comment puis-je commencer ?',
      'lp_chatbot_a4': 'Cliquez sur « Créer mon site » en haut à droite. Vous décrivez votre boutique en 7 étapes et l\'IA construit le reste — en 2 minutes.',
      'lp_chatbot_q5': 'L\'IA peut-elle vraiment construire mon site ?',
      'lp_chatbot_a5': 'Oui. Notre IA Claude Opus 4.5 génère pages, blocs, design et peut modifier votre site en parlant français, arabe ou anglais.',
      'lp_chatbot_q6': 'Mes données sont-elles sécurisées ?',
      'lp_chatbot_a6': 'Toutes vos données sont hébergées en Tunisie. Chiffrement SSL, sauvegardes quotidiennes, conformité aux meilleures pratiques.',
      'lp_chatbot_contact_email': 'Email',
      'lp_chatbot_contact_phone': 'Téléphone',
      'lp_chatbot_contact_hours_label': 'Horaires',
      'lp_chatbot_contact_hours_val': 'Lun-Ven : 8h-18h · Sam : 9h-15h',
      'lp_chatbot_input_placeholder': 'Posez votre question…',
      'lp_chatbot_send': 'Envoyer',
      'lp_chatbot_live_slow': 'Je transmets votre question à l\'équipe — vous aurez une réponse ici ou via le contact direct. 🙏',

      // ─── Vitrine simulée (visuels AtlasCloud) ───
      'lp_mock_hero_name': 'Sac cabas artisanal',
      'lp_mock_add_to_cart': 'Ajouter au panier',
      'lp_mock_p1': 'Bols céramique (x2)',
      'lp_mock_p2': 'Huile d\'olive premium',
      'lp_mock_p3': 'Robe lin été',
      'lp_mock_p4': 'Sandales cuir',
      'lp_mock_p5': 'Panier tressé',
      'lp_mock_p6': 'Bracelet argent',
      'lp_illu_chat_done': 'C\'est fait — preview live',

      // ─── Reels campagne (studio IA) ───
      'lp_reels_eyebrow': 'Vu sur vos réseaux',
      'lp_reels_title': 'Des spots produits par notre studio IA',
      'lp_reels_sub': 'La même technologie qui crée ces vidéos travaille pour votre boutique : scènes générées, voix off et sous-titres en 3 langues.',
      'lp_reels_day': 'Jour',
      'lp_reel_1_title': 'Lancez votre commerce',
      'lp_reel_1_caption': 'Boutique, commandes, messages et équipe — dans une seule app.',
      'lp_reel_5_title': 'La livraison maîtrisée',
      'lp_reel_5_caption': 'Transporteurs comparés, suivi en temps réel, encaissement sécurisé.',
      'lp_reel_7_title': 'Pilotez et grandissez',
      'lp_reel_7_caption': 'Tableaux de bord en temps réel et IA qui veille sur votre activité.',
      'lp_reels_note': 'Ces vidéos ont été générées par le Studio vidéo de vendeur.site — le même outil est inclus dans votre espace pour créer vos propres spots.'
    };

    this.translations['en'] = {
      'accueil': 'Home',
      'services': 'Services',
      'about': 'About Us',
      'contact': 'Contact',
      'connexion': 'Login',
      'hero_title': 'Transform Your Business in 2025',
      'hero_subtitle': 'AI & Data Solutions for Smart Commerce',
      'hero_description': 'Discover how Codincloud can propel your business into the future.',
      'start_free_trial': 'Start Free Trial',
      'features_title': 'Our Advanced Features',
      'features_subtitle': 'Designed for the future of e-commerce',
      'feature1_title': 'AI Installation',
      'feature1_desc': 'Set up your store in seconds with AI',
      'feature2_title': 'Predictive Analytics',
      'feature2_desc': 'Anticipate your customers\' needs with Machine Learning',
      'feature3_title': '24/7 Support',
      'feature3_desc': 'Our multilingual AI assistant is available continuously',
      'vision_title': 'Our Vision',
      'vision_subtitle': 'A global team, a unique mission',
      'vision_desc': 'We use quantum computing principles with user-centered design.',
      'stats_clients': 'Satisfied Clients',
      'stats_uptime': 'Uptime',
      'contact_title': 'Let\'s Discuss Your Future',
      'footer_copyright': '© 2026 vendeur.site — Powered by Codincloud',

      // ─── Public landing — vendeur.site (new) ───
      'lp_nav_features': 'Features',
      'lp_nav_steps': 'How it works',
      'lp_nav_pricing': 'Pricing',
      'lp_nav_testimonials': 'Reviews',
      'lp_nav_faq': 'FAQ',
      'lp_nav_login': 'Sign in',
      'lp_nav_signup': 'Create my site',

      'lp_hero_eyebrow': 'New · Claude Opus 4.5 AI built-in',
      'lp_hero_title_pre': 'Launch your online store in',
      'lp_hero_title_highlight': '2 minutes',
      'lp_hero_title_post': '— guided by AI',
      'lp_hero_subtitle': 'Vendeur.site combines a site builder, multi-channel inbox (Messenger, Instagram, WhatsApp, Marketplace), product/order/shipping management (TunisiaShip) and an AI assistant that codes your site for you. 100% Tunisian, 0% technical.',
      'lp_hero_cta_primary': 'Create my store for free',
      'lp_hero_cta_secondary': 'Watch a 60s demo',

      'lp_cinema_eyebrow': 'Immersive experience',
      'lp_cinema_title': 'Your store comes alive — click inside the video',
      'lp_cinema_sub': 'A video generated by our AI. The glowing dots are real buttons: explore, click, get started.',
      'lp_cinema_play': 'Start the experience',
      'lp_cinema_hs_signup': 'Create my store',
      'lp_cinema_hs_inbox': 'Multi-channel inbox',
      'lp_cinema_hs_studio': 'AI Studio in action',
      'lp_cinema_chap_1': 'The store assembles',
      'lp_cinema_chap_2': 'Channels connected',
      'lp_cinema_chap_3': 'AI takes over',
      'lp_hero_stat_vendors': 'Active sellers',
      'lp_hero_stat_setup': 'Minutes to create',
      'lp_hero_stat_ai': 'AI Claude built-in',

      'lp_trust_title': 'Trusted by',

      'lp_features_title': 'Everything you need to sell online',
      'lp_features_subtitle': 'One unified platform. No stack to assemble. No code to write.',
      'lp_feature_builder_title': 'AI Site Builder',
      'lp_feature_builder_desc': 'Describe your store, AI builds it. Edit it by speaking: "Add an FAQ", "Change the color to blue". Live preview.',
      'lp_feature_inbox_title': 'Multi-channel Inbox',
      'lp_feature_inbox_desc': 'Messenger + Instagram + WhatsApp + Marketplace in one screen. Reply 3× faster to your customers.',
      'lp_feature_360_title': '360° Management',
      'lp_feature_360_desc': 'Products, stock, orders, customers, employees, couriers. All connected, all in real time.',
      'lp_feature_shipping_title': 'Tunisia Shipping',
      'lp_feature_shipping_desc': 'Native TunisiaShip integration: labels, tracking, returns. Compare rates and print in one click.',
      'lp_feature_analytics_title': 'Analytics & RFM',
      'lp_feature_analytics_desc': 'Top products, top customers, conversion funnel, RFM segmentation. Infinite drill-down, CSV exports.',
      'lp_feature_versioning_title': 'Git-like Versioning',
      'lp_feature_versioning_desc': 'Every change is saved. Restore your site to a previous version in one click.',
      'lp_feature_tiktok_title': 'TikTok is coming soon',
      'lp_feature_tiktok_desc': 'Reels, AI hashtags, TikTok Ads, TikTok Shop, unified comments + DM inbox — the same experience as for Meta.',
      'lp_feature_soon_badge': 'Soon',
      'lp_feature_notify_me': 'Notify me',
      'lp_feature_learn_more': 'Learn more',

      'lp_steps_title': 'How it works',
      'lp_steps_subtitle': 'Four steps to go from idea to your online store.',
      'lp_step1_title': 'Describe your store',
      'lp_step1_desc': '7-step onboarding: name, store(s), template, branding, pages, integrations.',
      'lp_step2_title': 'AI builds your site',
      'lp_step2_desc': 'Claude Opus 4.5 generates pages and blocks, applies your brand, picks your best products.',
      'lp_step3_title': 'Refine by chatting',
      'lp_step3_desc': 'Conversational AI studio + live preview. "Add a -10% promo", "Make the font modern".',
      'lp_step4_title': 'Go live',
      'lp_step4_desc': 'One-click publishing. Free <you>.vendeur.site URL, custom domain available.',

      'lp_demo_title': 'See the AI in action',
      'lp_demo_subtitle': 'Click a suggestion below — the preview updates live.',
      'lp_demo_browser_url': 'mystore.vendeur.site',
      'lp_demo_ai_greeting': 'Hi! I\'m your assistant. What would you like to change?',
      'lp_demo_suggestion_color': 'Change the color to cyan',
      'lp_demo_suggestion_promo': 'Add a -10% promo',
      'lp_demo_suggestion_hero': 'Put this product in the hero',
      'lp_demo_user_color': 'Change the main color to cyan',
      'lp_demo_ai_color': 'Main color updated to cyan. The preview is refreshing.',
      'lp_demo_user_promo': 'Add a -10% promo to the homepage',
      'lp_demo_ai_promo': '-10% promo badge added at the top of your store.',
      'lp_demo_user_hero': 'Put this product in the hero',
      'lp_demo_ai_hero': 'Product promoted to the hero banner with a strong visual.',
      'lp_demo_powered': 'Powered by Claude Opus 4.5',

      'lp_pricing_title': 'Simple, transparent pricing',
      'lp_pricing_subtitle': 'Start free. Upgrade when you\'re ready.',
      'lp_pricing_popular': 'Most popular',
      'lp_plan_starter_name': 'Starter',
      'lp_plan_starter_price': 'Free',
      'lp_plan_starter_period': 'Forever',
      'lp_plan_starter_f1': '1 vendeur.site site',
      'lp_plan_starter_f2': '50 products',
      'lp_plan_starter_f3': '1 physical store',
      'lp_plan_starter_f4': 'Limited AI (50 msg/h)',
      'lp_plan_starter_f5': 'Community support',
      'lp_plan_starter_cta': 'Start for free',
      'lp_plan_pro_name': 'Pro',
      'lp_plan_pro_price': '49 TND',
      'lp_plan_pro_period': '/month',
      'lp_plan_pro_f1': 'Unlimited sites & products',
      'lp_plan_pro_f2': 'Claude Opus 4.5 AI',
      'lp_plan_pro_f3': 'Full multi-channel inbox',
      'lp_plan_pro_f4': 'TunisiaShip integrated',
      'lp_plan_pro_f5': 'Advanced RFM analytics',
      'lp_plan_pro_f6': 'Priority support',
      'lp_plan_pro_cta': '14-day trial',
      'lp_plan_business_name': 'Business',
      'lp_plan_business_price': 'Contact us',
      'lp_plan_business_period': 'Multi-vendor',
      'lp_plan_business_f1': 'Multi-vendor',
      'lp_plan_business_f2': 'Unlimited employees',
      'lp_plan_business_f3': 'API & webhooks',
      'lp_plan_business_f4': '24/7 dedicated support',
      'lp_plan_business_f5': 'On-site training',
      'lp_plan_business_cta': 'Contact us',

      'lp_testimonials_title': 'What our sellers say',
      'lp_testimonials_subtitle': 'Real stories, real sales.',
      'lp_testimonial_1_quote': 'I built my site in 1 hour and made my first sale the same evening. The AI explained everything simply.',
      'lp_testimonial_1_name': 'Sarra B.',
      'lp_testimonial_1_role': 'Fashion store, Tunis',
      'lp_testimonial_2_quote': 'The integrated WhatsApp chat is a game changer. Customers order directly without intermediaries.',
      'lp_testimonial_2_name': 'Mehdi T.',
      'lp_testimonial_2_role': 'Organic grocery, Sousse',
      'lp_testimonial_3_quote': 'TunisiaShip tracking saves me 1h/day. And the AI suggests which products to feature.',
      'lp_testimonial_3_name': 'Yasmine L.',
      'lp_testimonial_3_role': 'Cosmetics, Sfax',
      'lp_testimonial_4_quote': 'I used to spend hours on Photoshop. Now I describe what I want and it\'s done.',
      'lp_testimonial_4_name': 'Karim H.',
      'lp_testimonial_4_role': 'Crafts, Nabeul',
      'lp_testimonial_5_quote': 'The analytics helped me understand my top customers. My revenue doubled in 3 months.',
      'lp_testimonial_5_name': 'Amira K.',
      'lp_testimonial_5_role': 'Jewelry, Hammamet',

      'lp_faq_title': 'Frequently asked questions',
      'lp_faq_subtitle': 'Still hesitating? Here are the answers to the most asked questions.',
      'lp_faq_1_q': 'How much does vendeur.site cost?',
      'lp_faq_1_a': 'The Starter plan is free forever. The Pro plan is 49 TND/month (no commitment). Business is quoted based on your needs.',
      'lp_faq_2_q': 'Can the AI really build my site?',
      'lp_faq_2_a': 'Yes. Our assistant uses Claude Opus 4.5, the most advanced AI model for code. It generates pages, blocks, applies your brand and can edit your site in French, Arabic or English.',
      'lp_faq_3_q': 'Is my data secure?',
      'lp_faq_3_a': 'All your data (products, customers, conversations) is hosted in Tunisia on dedicated infrastructure. SSL encryption, daily backups, best-practice compliance.',
      'lp_faq_4_q': 'Can I connect my own domain?',
      'lp_faq_4_a': 'Of course. You get a free <you>.vendeur.site URL, and you can connect your own domain (e.g. mystore.tn) starting from the Pro plan.',
      'lp_faq_5_q': 'Do you support Konnect / Stripe?',
      'lp_faq_5_a': 'Konnect (local Tunisian payment) is integrated. Stripe is coming soon. Cash on delivery is natively supported via TunisiaShip.',
      'lp_faq_6_q': 'Which social channels do you support?',
      'lp_faq_6_a': 'Facebook Messenger, Instagram Direct, WhatsApp Business (official Cloud API) and Facebook Marketplace. All your conversations unified in a single inbox.',
      'lp_faq_7_q': 'How is the AI billed?',
      'lp_faq_7_a': 'AI is included in the Pro plan (unlimited at reasonable use). No surprises on your bill, it\'s transparent.',

      'lp_cta_final_title': 'Ready to launch your store?',
      'lp_cta_final_subtitle': '2 minutes to create. Years to grow.',
      'lp_cta_final_primary': 'Create my store',
      'lp_cta_final_secondary': 'Request a demo',

      'lp_footer_tagline': 'The all-in-one Tunisian platform for e-commerce sellers, powered by AI.',
      'lp_footer_col_product': 'Product',
      'lp_footer_col_resources': 'Resources',
      'lp_footer_col_legal': 'Legal',
      'lp_footer_link_builder': 'Site builder',
      'lp_footer_link_inbox': 'Inbox',
      'lp_footer_link_analytics': 'Analytics',
      'lp_footer_link_shipping': 'TunisiaShip',
      'lp_footer_link_pricing': 'Pricing',
      'lp_footer_link_docs': 'Documentation',
      'lp_footer_link_templates': 'Templates',
      'lp_footer_link_blog': 'Blog',
      'lp_footer_link_changelog': 'Changelog',
      'lp_footer_link_terms': 'Terms',
      'lp_footer_link_privacy': 'Privacy',
      'lp_footer_link_cookies': 'Cookies',
      'lp_footer_link_contact': 'Contact',

      'lp_chatbot_title': 'vendeur.site Assistant',
      'lp_chatbot_status': 'Online',
      'lp_chatbot_powered': 'Powered by Claude Opus',
      'lp_chatbot_quick_questions': 'Quick questions',
      'lp_chatbot_contact_direct': 'Direct contact',
      'lp_chatbot_contact_title': 'Contact us directly',
      'lp_chatbot_welcome': 'Hi! I\'m the vendeur.site assistant. How can I help you launch your store?',
      'lp_chatbot_q1': 'What are your main services?',
      'lp_chatbot_a1': 'Vendeur.site is an all-in-one platform: AI site builder, multi-channel inbox (Messenger, Instagram, WhatsApp, Marketplace), product/order/shipping management, TunisiaShip integration and advanced analytics.',
      'lp_chatbot_q2': 'How much does it cost?',
      'lp_chatbot_a2': 'Discovery: 0 TND/month (7% on sales). Starter: 29 TND/month (5%). Pro: 99 TND/month (3%). Enterprise: 299 TND/month (2%). No commitment.',
      'lp_chatbot_q3': 'Do you offer a free trial?',
      'lp_chatbot_a3': 'Yes! Signing up is free and the Discovery plan costs 0 TND/month — you only pay a commission on sales. No commitment.',
      'lp_chatbot_q4': 'How can I get started?',
      'lp_chatbot_a4': 'Click "Create my site" at the top right. Describe your store in 7 steps and the AI builds the rest — in 2 minutes.',
      'lp_chatbot_q5': 'Can the AI really build my site?',
      'lp_chatbot_a5': 'Yes. Our Claude Opus 4.5 AI generates pages, blocks, design and can edit your site in French, Arabic or English.',
      'lp_chatbot_q6': 'Is my data secure?',
      'lp_chatbot_a6': 'All your data is hosted in Tunisia. SSL encryption, daily backups, best-practice compliance.',
      'lp_chatbot_contact_email': 'Email',
      'lp_chatbot_contact_phone': 'Phone',
      'lp_chatbot_contact_hours_label': 'Hours',
      'lp_chatbot_contact_hours_val': 'Mon-Fri: 8am-6pm · Sat: 9am-3pm',
      'lp_chatbot_input_placeholder': 'Ask your question…',
      'lp_chatbot_send': 'Send',
      'lp_chatbot_live_slow': 'I\'m forwarding your question to the team — you\'ll get an answer here or via direct contact. 🙏',

      // ─── Simulated storefront (AtlasCloud visuals) ───
      'lp_mock_hero_name': 'Handcrafted tote bag',
      'lp_mock_add_to_cart': 'Add to cart',
      'lp_mock_p1': 'Ceramic bowls (x2)',
      'lp_mock_p2': 'Premium olive oil',
      'lp_mock_p3': 'Linen summer dress',
      'lp_mock_p4': 'Leather sandals',
      'lp_mock_p5': 'Woven basket bag',
      'lp_mock_p6': 'Silver bracelet',
      'lp_illu_chat_done': 'Done — live preview',

      // ─── Campaign reels (AI studio) ───
      'lp_reels_eyebrow': 'Seen on your feeds',
      'lp_reels_title': 'Spots produced by our AI studio',
      'lp_reels_sub': 'The same technology that made these videos works for your store: generated scenes, voice-over and subtitles in 3 languages.',
      'lp_reels_day': 'Day',
      'lp_reel_1_title': 'Launch your business',
      'lp_reel_1_caption': 'Store, orders, messages and team — in a single app.',
      'lp_reel_5_title': 'Delivery, mastered',
      'lp_reel_5_caption': 'Carriers compared, real-time tracking, secure cash collection.',
      'lp_reel_7_title': 'Steer and grow',
      'lp_reel_7_caption': 'Real-time dashboards and an AI that watches over your business.',
      'lp_reels_note': 'These videos were generated by the vendeur.site Video Studio — the same tool is included in your workspace to create your own spots.'
    };

    this.translations['ar'] = {
      'accueil': 'الرئيسية',
      'services': 'الخدمات',
      'about': 'من نحن',
      'contact': 'اتصل بنا',
      'connexion': 'تسجيل الدخول',
      'hero_title': 'حوّل أعمالك في عام 2025',
      'hero_subtitle': 'حلول الذكاء الاصطناعي والبيانات للتجارة الذكية',
      'hero_description': 'اكتشف كيف يمكن لـ Codincloud دفع عملك نحو المستقبل.',
      'start_free_trial': 'ابدأ الاختبار المجاني',
      'features_title': 'ميزاتنا المتقدمة',
      'features_subtitle': 'مصممة لمستقبل التجارة الإلكترونية',
      'feature1_title': 'التثبيت بالذكاء الاصطناعي',
      'feature1_desc': 'قم بإعداد متجرك في ثوانٍ باستخدام الذكاء الاصطناعي',
      'feature2_title': 'التحليلات التنبؤية',
      'feature2_desc': 'توقع احتياجات عملائك من خلال تعلم الآلة',
      'feature3_title': 'دعم على مدار الساعة',
      'feature3_desc': 'مساعدنا الذكي متعدد اللغات متاح باستمرار',
      'vision_title': 'رؤيتنا',
      'vision_subtitle': 'فريق عالمي، مهمة فريدة',
      'vision_desc': 'نستخدم مبادئ الحوسبة الكمومية مع تصميم يركز على المستخدم.',
      'stats_clients': 'العملاء الراضين',
      'stats_uptime': 'وقت التشغيل',
      'contact_title': 'لنناقش مستقبلك',
      'footer_copyright': '© 2026 vendeur.site — مدعوم من Codincloud',

      // ─── الصفحة الرئيسية العامة vendeur.site (جديد) ───
      'lp_nav_features': 'الميزات',
      'lp_nav_steps': 'كيف يعمل',
      'lp_nav_pricing': 'الأسعار',
      'lp_nav_testimonials': 'الشهادات',
      'lp_nav_faq': 'الأسئلة الشائعة',
      'lp_nav_login': 'تسجيل الدخول',
      'lp_nav_signup': 'إنشاء موقعي',

      'lp_hero_eyebrow': 'جديد · ذكاء اصطناعي Claude Opus 4.5 مدمج',
      'lp_hero_title_pre': 'أطلق متجرك الإلكتروني في',
      'lp_hero_title_highlight': 'دقيقتين',
      'lp_hero_title_post': '— بإرشاد الذكاء الاصطناعي',
      'lp_hero_subtitle': 'vendeur.site يجمع منشئ المواقع، صندوق الوارد متعدد القنوات (Messenger، Instagram، WhatsApp، Marketplace)، إدارة المنتجات/الطلبات/الشحن (TunisiaShip) ومساعد ذكاء اصطناعي يبرمج موقعك من أجلك. 100٪ تونسي، 0٪ تقني.',
      'lp_hero_cta_primary': 'أنشئ متجري مجاناً',
      'lp_hero_cta_secondary': 'شاهد عرضاً في 60 ثانية',

      'lp_cinema_eyebrow': 'تجربة غامرة',
      'lp_cinema_title': 'متجرك ينبض بالحياة — انقر داخل الفيديو',
      'lp_cinema_sub': 'فيديو من إنتاج ذكائنا الاصطناعي. النقاط المضيئة أزرار حقيقية: استكشف، انقر، وانطلق.',
      'lp_cinema_play': 'ابدأ التجربة',
      'lp_cinema_hs_signup': 'أنشئ متجري',
      'lp_cinema_hs_inbox': 'صندوق وارد متعدد القنوات',
      'lp_cinema_hs_studio': 'استوديو الذكاء الاصطناعي',
      'lp_cinema_chap_1': 'المتجر يتشكّل',
      'lp_cinema_chap_2': 'القنوات متصلة',
      'lp_cinema_chap_3': 'الذكاء الاصطناعي يقود',
      'lp_hero_stat_vendors': 'بائعون نشطون',
      'lp_hero_stat_setup': 'دقائق للإنشاء',
      'lp_hero_stat_ai': 'ذكاء اصطناعي Claude',

      'lp_trust_title': 'يثقون بنا',

      'lp_features_title': 'كل ما تحتاجه للبيع عبر الإنترنت',
      'lp_features_subtitle': 'منصة موحدة. لا حاجة لتجميع تقنيات. لا حاجة لكتابة أكواد.',
      'lp_feature_builder_title': 'منشئ المواقع بالذكاء الاصطناعي',
      'lp_feature_builder_desc': 'صف متجرك، والذكاء الاصطناعي يبنيه. عدّل بالكلام: «أضف أسئلة شائعة»، «غيّر اللون إلى الأزرق». معاينة مباشرة.',
      'lp_feature_inbox_title': 'صندوق وارد متعدد القنوات',
      'lp_feature_inbox_desc': 'Messenger + Instagram + WhatsApp + Marketplace في شاشة واحدة. ردّ على عملائك 3 أضعاف أسرع.',
      'lp_feature_360_title': 'إدارة 360°',
      'lp_feature_360_desc': 'منتجات، مخزون، طلبات، عملاء، موظفون، شركاء التوصيل. كل شيء متصل، كل شيء في الوقت الحقيقي.',
      'lp_feature_shipping_title': 'الشحن في تونس',
      'lp_feature_shipping_desc': 'تكامل أصلي مع TunisiaShip: ملصقات، تتبع، إرجاع. قارن الأسعار واطبع بنقرة واحدة.',
      'lp_feature_analytics_title': 'التحليلات و RFM',
      'lp_feature_analytics_desc': 'أفضل المنتجات، أفضل العملاء، قمع التحويل، تجزئة RFM. تعمق لا نهائي، تصدير CSV.',
      'lp_feature_versioning_title': 'إصدارات بأسلوب Git',
      'lp_feature_versioning_desc': 'يتم حفظ كل تعديل. استعد موقعك إلى نسخة سابقة بنقرة واحدة.',
      'lp_feature_tiktok_title': 'تيك توك قريبًا',
      'lp_feature_tiktok_desc': 'ريلز، هاشتاڨ بالذكاء الاصطناعي، إشهار تيك توك، TikTok Shop، إنبوكس موحّد للكومنتيرات و DM — نفس التجربة كيما Meta.',
      'lp_feature_soon_badge': 'قريبًا',
      'lp_feature_notify_me': 'نبّهني',
      'lp_feature_learn_more': 'اعرف المزيد',

      'lp_steps_title': 'كيف يعمل',
      'lp_steps_subtitle': 'أربع خطوات للانتقال من الفكرة إلى متجرك الإلكتروني.',
      'lp_step1_title': 'صف متجرك',
      'lp_step1_desc': 'استكشاف من 7 خطوات: الاسم، المتجر(المتاجر)، القالب، العلامة التجارية، الصفحات، التكامل.',
      'lp_step2_title': 'الذكاء الاصطناعي يبني موقعك',
      'lp_step2_desc': 'Claude Opus 4.5 يولّد الصفحات والكتل، يطبق هويتك، يختار أفضل منتجاتك.',
      'lp_step3_title': 'حسّن بالمحادثة',
      'lp_step3_desc': 'استوديو ذكاء اصطناعي تفاعلي + معاينة مباشرة. «أضف خصم -10٪»، «غيّر الخط إلى عصري».',
      'lp_step4_title': 'انطلق',
      'lp_step4_desc': 'نشر بنقرة واحدة. عنوان URL مجاني <you>.vendeur.site، مع إمكانية ربط نطاق مخصص.',

      'lp_demo_title': 'شاهد الذكاء الاصطناعي بالعمل',
      'lp_demo_subtitle': 'انقر على اقتراح أدناه — تتحدث المعاينة مباشرة.',
      'lp_demo_browser_url': 'mystore.vendeur.site',
      'lp_demo_ai_greeting': 'مرحباً! أنا مساعدك. ماذا تريد أن تغير؟',
      'lp_demo_suggestion_color': 'غيّر اللون إلى السماوي',
      'lp_demo_suggestion_promo': 'أضف خصم -10٪',
      'lp_demo_suggestion_hero': 'ضع هذا المنتج في الواجهة',
      'lp_demo_user_color': 'غيّر اللون الرئيسي إلى السماوي',
      'lp_demo_ai_color': 'تم تحديث اللون الرئيسي إلى السماوي. المعاينة تتحدث.',
      'lp_demo_user_promo': 'أضف خصم -10٪ على الصفحة الرئيسية',
      'lp_demo_ai_promo': 'تمت إضافة شارة خصم -10٪ في أعلى متجرك.',
      'lp_demo_user_hero': 'ضع هذا المنتج في الواجهة',
      'lp_demo_ai_hero': 'تم ترقية المنتج إلى لافتة الواجهة مع صورة قوية.',
      'lp_demo_powered': 'مدعوم من Claude Opus 4.5',

      'lp_pricing_title': 'أسعار بسيطة وشفافة',
      'lp_pricing_subtitle': 'ابدأ مجاناً. ارتقِ عندما تكون مستعداً.',
      'lp_pricing_popular': 'الأكثر شعبية',
      'lp_plan_starter_name': 'مبتدئ',
      'lp_plan_starter_price': 'مجاني',
      'lp_plan_starter_period': 'إلى الأبد',
      'lp_plan_starter_f1': 'موقع vendeur.site واحد',
      'lp_plan_starter_f2': '50 منتج',
      'lp_plan_starter_f3': 'متجر فعلي واحد',
      'lp_plan_starter_f4': 'ذكاء اصطناعي محدود (50 رسالة/س)',
      'lp_plan_starter_f5': 'دعم المجتمع',
      'lp_plan_starter_cta': 'ابدأ مجاناً',
      'lp_plan_pro_name': 'احترافي',
      'lp_plan_pro_price': '49 د.ت',
      'lp_plan_pro_period': '/شهر',
      'lp_plan_pro_f1': 'مواقع ومنتجات غير محدودة',
      'lp_plan_pro_f2': 'ذكاء اصطناعي Claude Opus 4.5',
      'lp_plan_pro_f3': 'صندوق وارد متعدد القنوات كامل',
      'lp_plan_pro_f4': 'TunisiaShip مدمج',
      'lp_plan_pro_f5': 'تحليلات RFM متقدمة',
      'lp_plan_pro_f6': 'دعم بأولوية',
      'lp_plan_pro_cta': 'تجربة 14 يوماً',
      'lp_plan_business_name': 'الأعمال',
      'lp_plan_business_price': 'حسب الطلب',
      'lp_plan_business_period': 'متعدد البائعين',
      'lp_plan_business_f1': 'متعدد البائعين',
      'lp_plan_business_f2': 'موظفون غير محدودين',
      'lp_plan_business_f3': 'API و webhooks',
      'lp_plan_business_f4': 'دعم مخصص 24/7',
      'lp_plan_business_f5': 'تدريب في الموقع',
      'lp_plan_business_cta': 'تواصل معنا',

      'lp_testimonials_title': 'ماذا يقول بائعونا',
      'lp_testimonials_subtitle': 'قصص حقيقية، مبيعات حقيقية.',
      'lp_testimonial_1_quote': 'أنشأت موقعي في ساعة وحققت أول بيع في نفس المساء. الذكاء الاصطناعي شرح كل شيء ببساطة.',
      'lp_testimonial_1_name': 'سارّة ب.',
      'lp_testimonial_1_role': 'بوتيك أزياء، تونس',
      'lp_testimonial_2_quote': 'محادثة WhatsApp المدمجة غيّرت اللعبة. يطلب عملائي مباشرة دون وسطاء.',
      'lp_testimonial_2_name': 'مهدي ت.',
      'lp_testimonial_2_role': 'بقالة عضوية، سوسة',
      'lp_testimonial_3_quote': 'تتبع TunisiaShip يوفر لي ساعة كل يوم. والذكاء الاصطناعي يقترح المنتجات التي يجب إبرازها.',
      'lp_testimonial_3_name': 'ياسمين ل.',
      'lp_testimonial_3_role': 'مستحضرات تجميل، صفاقس',
      'lp_testimonial_4_quote': 'كنت أقضي ساعات على Photoshop. الآن أصف ما أريد وينفّذ.',
      'lp_testimonial_4_name': 'كريم ح.',
      'lp_testimonial_4_role': 'حرف يدوية، نابل',
      'lp_testimonial_5_quote': 'التحليلات ساعدتني على فهم أفضل عملائي. تضاعفت إيراداتي في 3 أشهر.',
      'lp_testimonial_5_name': 'أميرة ك.',
      'lp_testimonial_5_role': 'مجوهرات، الحمامات',

      'lp_faq_title': 'الأسئلة الشائعة',
      'lp_faq_subtitle': 'ما زلت متردداً؟ إليك إجابات على الأسئلة الأكثر طرحاً.',
      'lp_faq_1_q': 'كم تكلفة vendeur.site؟',
      'lp_faq_1_a': 'خطة Starter مجانية إلى الأبد. خطة Pro بسعر 49 د.ت/شهر (دون التزام). Business حسب الطلب.',
      'lp_faq_2_q': 'هل يمكن للذكاء الاصطناعي حقاً بناء موقعي؟',
      'lp_faq_2_a': 'نعم. مساعدنا يستخدم Claude Opus 4.5، النموذج الأكثر تقدماً للكود. يولّد الصفحات والكتل ويمكنه تعديل موقعك بالفرنسية أو العربية أو الإنجليزية.',
      'lp_faq_3_q': 'هل بياناتي آمنة؟',
      'lp_faq_3_a': 'جميع بياناتك (المنتجات، العملاء، المحادثات) مستضافة في تونس على بنية تحتية مخصصة. تشفير SSL، نسخ احتياطية يومية.',
      'lp_faq_4_q': 'هل يمكنني ربط نطاقي الخاص؟',
      'lp_faq_4_a': 'بالطبع. تحصل على عنوان URL مجاني <you>.vendeur.site، ويمكنك ربط نطاقك الخاص (مثل mystore.tn) ابتداءً من خطة Pro.',
      'lp_faq_5_q': 'هل تدعمون Konnect / Stripe؟',
      'lp_faq_5_a': 'Konnect (دفع تونسي محلي) مدمج. Stripe قادم قريباً. الدفع عند الاستلام مدعوم أصلياً عبر TunisiaShip.',
      'lp_faq_6_q': 'ما القنوات الاجتماعية المدعومة؟',
      'lp_faq_6_a': 'Facebook Messenger، Instagram Direct، WhatsApp Business (Cloud API الرسمي) و Facebook Marketplace. جميع محادثاتك موحدة في صندوق وارد واحد.',
      'lp_faq_7_q': 'كيف يتم تسعير الذكاء الاصطناعي؟',
      'lp_faq_7_a': 'الذكاء الاصطناعي مشمول في خطة Pro (غير محدود بحدود معقولة). لا مفاجآت على الفاتورة، شفاف.',

      'lp_cta_final_title': 'مستعد لإطلاق متجرك؟',
      'lp_cta_final_subtitle': 'دقيقتان للإنشاء. سنوات للنمو.',
      'lp_cta_final_primary': 'أنشئ متجري',
      'lp_cta_final_secondary': 'اطلب عرضاً توضيحياً',

      'lp_footer_tagline': 'منصة تونسية شاملة لبائعي التجارة الإلكترونية، مدعومة بالذكاء الاصطناعي.',
      'lp_footer_col_product': 'المنتج',
      'lp_footer_col_resources': 'الموارد',
      'lp_footer_col_legal': 'قانوني',
      'lp_footer_link_builder': 'منشئ المواقع',
      'lp_footer_link_inbox': 'صندوق الوارد',
      'lp_footer_link_analytics': 'التحليلات',
      'lp_footer_link_shipping': 'TunisiaShip',
      'lp_footer_link_pricing': 'الأسعار',
      'lp_footer_link_docs': 'الوثائق',
      'lp_footer_link_templates': 'القوالب',
      'lp_footer_link_blog': 'المدونة',
      'lp_footer_link_changelog': 'سجل التغييرات',
      'lp_footer_link_terms': 'الشروط',
      'lp_footer_link_privacy': 'الخصوصية',
      'lp_footer_link_cookies': 'ملفات تعريف الارتباط',
      'lp_footer_link_contact': 'تواصل',

      'lp_chatbot_title': 'مساعد vendeur.site',
      'lp_chatbot_status': 'متصل',
      'lp_chatbot_powered': 'مدعوم من Claude Opus',
      'lp_chatbot_quick_questions': 'أسئلة سريعة',
      'lp_chatbot_contact_direct': 'تواصل مباشر',
      'lp_chatbot_contact_title': 'تواصل معنا مباشرة',
      'lp_chatbot_welcome': 'مرحباً! أنا مساعد vendeur.site. كيف يمكنني مساعدتك على إطلاق متجرك؟',
      'lp_chatbot_q1': 'ما هي خدماتكم الرئيسية؟',
      'lp_chatbot_a1': 'vendeur.site منصة شاملة: منشئ مواقع بالذكاء الاصطناعي، صندوق وارد متعدد القنوات، إدارة المنتجات/الطلبات/الشحن، تكامل TunisiaShip وتحليلات متقدمة.',
      'lp_chatbot_q2': 'كم تكلف خدماتكم؟',
      'lp_chatbot_a2': 'Découverte: 0 د.ت/شهر (عمولة 7% على المبيعات). Starter: 29 د.ت/شهر (5%). Pro: 99 د.ت/شهر (3%). Enterprise: 299 د.ت/شهر (2%). دون التزام.',
      'lp_chatbot_q3': 'هل تقدمون تجربة مجانية؟',
      'lp_chatbot_a3': 'نعم! التسجيل مجاني وخطة Découverte بـ0 د.ت/شهر — تدفع عمولة على المبيعات فقط. دون أي التزام.',
      'lp_chatbot_q4': 'كيف يمكنني البدء؟',
      'lp_chatbot_a4': 'انقر على «أنشئ موقعي» في الأعلى. صف متجرك في 7 خطوات والذكاء الاصطناعي يبني الباقي — في دقيقتين.',
      'lp_chatbot_q5': 'هل يمكن للذكاء الاصطناعي حقاً بناء موقعي؟',
      'lp_chatbot_a5': 'نعم. ذكاؤنا الاصطناعي Claude Opus 4.5 يولّد الصفحات والكتل والتصميم ويمكنه تعديل موقعك بالفرنسية أو العربية أو الإنجليزية.',
      'lp_chatbot_q6': 'هل بياناتي آمنة؟',
      'lp_chatbot_a6': 'جميع بياناتك مستضافة في تونس. تشفير SSL، نسخ احتياطية يومية.',
      'lp_chatbot_contact_email': 'البريد الإلكتروني',
      'lp_chatbot_contact_phone': 'الهاتف',
      'lp_chatbot_contact_hours_label': 'ساعات العمل',
      'lp_chatbot_contact_hours_val': 'الإثنين-الجمعة: 8ص-6م · السبت: 9ص-3م',
      'lp_chatbot_input_placeholder': 'اكتب سؤالك…',
      'lp_chatbot_send': 'إرسال',
      'lp_chatbot_live_slow': 'أُحوّل سؤالك إلى الفريق — ستصلك الإجابة هنا أو عبر التواصل المباشر. 🙏',

      // ─── الواجهة التجريبية (صور AtlasCloud) ───
      'lp_mock_hero_name': 'حقيبة يد حرفية',
      'lp_mock_add_to_cart': 'أضف إلى السلة',
      'lp_mock_p1': 'أواني خزفية (2)',
      'lp_mock_p2': 'زيت زيتون فاخر',
      'lp_mock_p3': 'فستان كتان صيفي',
      'lp_mock_p4': 'صنادل جلدية',
      'lp_mock_p5': 'سلة مضفورة',
      'lp_mock_p6': 'سوار فضي',
      'lp_illu_chat_done': 'تمّ — معاينة مباشرة',

      // ─── مقاطع الحملة (استوديو الذكاء الاصطناعي) ───
      'lp_reels_eyebrow': 'كما شاهدتموها على الشبكات',
      'lp_reels_title': 'مقاطع من إنتاج استوديو الذكاء الاصطناعي',
      'lp_reels_sub': 'نفس التقنية التي أنتجت هذه الفيديوهات تعمل لمتجرك: مشاهد مولّدة، تعليق صوتي وترجمات بثلاث لغات.',
      'lp_reels_day': 'اليوم',
      'lp_reel_1_title': 'أطلق تجارتك',
      'lp_reel_1_caption': 'متجر، طلبيات، رسائل وفريق — في تطبيق واحد.',
      'lp_reel_5_title': 'توصيل تحت السيطرة',
      'lp_reel_5_caption': 'مقارنة الناقلين، تتبّع فوري، وتحصيل آمن عند التسليم.',
      'lp_reel_7_title': 'قُد وانمُ',
      'lp_reel_7_caption': 'لوحات قيادة فورية وذكاء اصطناعي يسهر على نشاطك.',
      'lp_reels_note': 'هذه الفيديوهات أُنتجت باستوديو الفيديو في vendeur.site — نفس الأداة متوفرة في فضائك لإنشاء مقاطعك الخاصة.'
    };
  }

  translate(key: string): string {
    const currentLang = this.currentLanguageSubject.value.code;
    return this.translations[currentLang][key] || key;
  }
}